import { ChevronDoubleDownIcon, ChevronDoubleUpIcon } from '@heroicons/react/24/outline';
import { isEmpty } from 'radash';
import { useCallback, useEffect, useState } from 'react';

import { LOCAL_STORAGE, MEDIA_TYPE_ONLINE_CATALOG } from '@/constants';
import { loadAdditionalSectionAttributes } from '@/dialogs/compare-items/compare-items-dialog-utils';
import useAdditionalCategoryAttributeService from '@/hooks/useAdditionalCategoryAttributeService';
import { useTranslation } from '@/hooks/useTranslation';
import { getFilter, getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { DataModelAttributeTypeName, PersistedItem } from '@/types/DataModel';

import CatalogAttribute from '../catalog-attributes/CatalogAttribute';
import ThumbsGallery from '../catalog-attributes/ThumbsGallery';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../core/Accordion';
import { Icon } from '../core/Icon';
import { Loader } from '../core/Loader';
import { useItemDetails } from './ItemDetails.queries';
import ReviewPanel from './ReviewPanel';

interface ItemDetailsComponentProps {
    itemData?: PersistedItem;
    primaryKey: string;
    isInEditorMode: boolean;
    prepareItemReview?: (item: PersistedItem) => void;
}

function ItemDetails({
    itemData,
    primaryKey,
    isInEditorMode,
    prepareItemReview
}: ItemDetailsComponentProps) {

    const $angularJSInjector = getInjector();
    const $rootScope = getRootScope($angularJSInjector);
    const showReviewPanel = localStorage.getItem(LOCAL_STORAGE.SHOW_REVIEW_KEY);
    const [item, setItem] = useState<PersistedItem | undefined>(itemData);
    const [isLoading, setIsLoading] = useState<boolean>(!itemData);

    const { data: fetchedItem, isPending: isLoadingPersistedItem } = useItemDetails({
        variables: { primaryKey__ : primaryKey },
        enabled: !itemData,
    });

    const { additionalCategoryAttributeService } = useAdditionalCategoryAttributeService();
    const { t } = useTranslation();

    const [sections, setSections] = useState<any[]>([]);
    const [images, setImages] = useState<any[]>([]);
    const [documents, setDocuments] = useState<any[]>([]);
    const [media, setMedia] = useState<any[]>([]);

    const [openedSections, setOpenedSections] = useState<string[]>([]);

    const translateSection = (section: any) => {
        const translatedSectionName = t('section.' + section.name);
        return translatedSectionName.includes('section.') ? section.label : translatedSectionName;
    };

    const handleExpandAll = useCallback(() => {
        setOpenedSections(sections.map((section: any) => section.name));
    }, [sections]);

    const handleCollapseAll = useCallback(() => {
        setOpenedSections([]);
    }, []);

    const handleToggleSection = (sectionName: string) => {
        setOpenedSections((prev) =>
            prev.includes(sectionName)
                ? prev.filter((name) => name !== sectionName)
                : [...prev, sectionName]
        );
    };

    const [reviewPanelVisible, setReviewPanelVisible] = useState(showReviewPanel === 'true');
    useEffect(() => {
        if (itemData) {
            setItem(itemData);
        } else if (fetchedItem) {
            setIsLoading(isLoadingPersistedItem);
            setItem(fetchedItem);
        }
    }, [itemData, fetchedItem]);

    useEffect(() => {
        // Listen for changes in showReviewPanel visibility
        //TOFIX remove this when we migrate all the item details page
        $rootScope.$on('showReviewPanelChanged', function (_event: any, result: any) {
            setReviewPanelVisible(result);
        });

        const fetchData = async () => {
            try {
                if (additionalCategoryAttributeService) {
                    const sections = await loadAdditionalSectionAttributes(
                        'detail',
                        item,
                        additionalCategoryAttributeService,
                        $rootScope,
                        'detail',
                    );
                    if (item) {
                        sections.map((section) => {
                            section.attributes.map((attribute: { typeName: string; name: string | number; label: string }) => {
                                if (attribute.typeName == DataModelAttributeTypeName.Collection || attribute.typeName == DataModelAttributeTypeName.Group) {
                                    addCollection(item[attribute.name], attribute);
                                } else if (attribute.typeName == DataModelAttributeTypeName.Image) {
                                    addImage(item[attribute.name], attribute);
                                } else if (attribute.typeName == DataModelAttributeTypeName.Document) {
                                    const file = item[attribute.name];
                                    if (file !== undefined) {
                                        if (getFilter('isFileType')(file, 'drawable')) {
                                            addImage(file, attribute);
                                        } else {
                                            addDocument(file, attribute);
                                        }
                                    }
                                }
                            });
                        });
                    }
                    return sections;
                }
            } catch (error) {
                logger.error('CompareItemsDialog:', error);
            }
        };

        if (item) {
            if (prepareItemReview) {
                prepareItemReview(item);
            }
            fetchData().then((result: any) => {
                setSections(result);
                setOpenedSections(result?.map((section: any) => section.name));
            });
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [additionalCategoryAttributeService, item]);

    useEffect(() => {
        setMedia([...images, ...documents]);
    }, [images, documents]);

    const addCollection = (collection: any, attribute: any) => {
        if (item && collection && collection.length > 0) {
            collection.map((row: any, index: number) => {
                const memberAttributes = $rootScope.dataModel.getMemberAttributes(attribute);
                memberAttributes.map((attr: any) => {
                    if (attr.typeName == 'Image') {
                        addImage(row[attr.name], attr);
                    } else if (attr.typeName == 'Document') {
                        const file = row[attr.name];
                        if (file && !isEmpty(file)) {
                            if (getFilter('isFileType')(file, 'drawable')) {
                                addImage(file, attr);
                            } else {
                                addDocument(row[attr.name], attr);
                            }
                        }
                    } else if (attr.typeName == 'Collection') {
                        addCollection(item[attribute.name][index][attr.name], attr);
                    }
                });
            });
        }
    };

    const addImage = (file: string, attribute: { label: string }) => {
        if (!file) {
            return;
        }
        if (file && (file.startsWith('http'))) {
            setImages((prevState) => [
                {
                    url: file.replace('\\', ''),
                    name: attribute.label,
                    type: MEDIA_TYPE_ONLINE_CATALOG.IMAGE,
                },
                ...prevState,
            ]);
        }
    };

    const addDocument = (file: string, attribute: { label: string }) => {
        if (!file) {
            return;
        }
        if (file && file.startsWith('http')) {
            setDocuments((prevState) => [
                {
                    url: file.replace('\\', ''),
                    name: attribute.label,
                    type: MEDIA_TYPE_ONLINE_CATALOG.DOCUMENT,
                },
                ...prevState,
            ]);
        }
    };

    return (
        <div className={`tw-bg-white tw-box-content tw-flex-1 tw-p-4 tw-mb-3 ${isInEditorMode ? 'tw-p-0' : 'tw-overflow-auto'}`}>
            {isLoading && !isEmpty(sections) ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-p-4">
                    <Loader />
                </div>
            ) : (
                item && (
                    <div className="tw-grid tw-grid-cols-12 tw-gap-4">
                        {reviewPanelVisible && !isInEditorMode && (
                            <div className="tw-col-span-3">
                                <ReviewPanel primaryKey={item.primaryKey__} item={item} />
                            </div>
                        )}
                        <div className={reviewPanelVisible && !isInEditorMode ? 'tw-col-span-9' : 'tw-col-span-12'}>
                            <div>
                                <div className="tw-w-full tw-flex tw-justify-end">
                                    <a onClick={handleExpandAll} className="tw-p-2">
                                        <span className="tw-flex tw-items-center">
                                            <span className="tw-mr-2">{t('ONLINE_CATALOG.EXPAND_ALL')}</span>
                                            <Icon icon={ChevronDoubleDownIcon} size="sm" variant="secondary" />
                                        </span>
                                    </a>
                                    <a onClick={handleCollapseAll} className="tw-p-2">
                                        <span className="tw-flex tw-items-center">
                                            <span className="tw-mr-2">{t('ONLINE_CATALOG.COLLAPSE_ALL')}</span>
                                            <Icon icon={ChevronDoubleUpIcon} size="sm" variant="secondary" />
                                        </span>
                                    </a>
                                </div>
                            </div>

                            {sections && !isEmpty(sections) && (
                                <Accordion
                                    type="multiple"
                                    className="tw-w-full"
                                    value={openedSections}
                                >
                                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                        {!isEmpty(media) && (
                                            <div className="tw-col-lg-6 tw-col-md-6 tw-print-hidden">
                                                <ThumbsGallery media={media} itemTitle={item.primaryKey__} />
                                            </div>
                                        )}

                                        {sections.map((section: any, index: number) => {
                                            // Check if any attribute in the section has a non-empty value in the item
                                            const hasValidAttributes = section.attributes.some(
                                                (a: any) => item[a.name] !== '' && item[a.name] !== undefined
                                            );

                                            if (hasValidAttributes) {
                                                return (
                                                    <AccordionItem
                                                        value={section.name}
                                                        key={index}
                                                        className="tw-w-full"
                                                    >
                                                        <AccordionTrigger
                                                            className="!tw-no-underline"
                                                            onClick={() => handleToggleSection(section.name)}
                                                        >
                                                            <span className="tw-text-[#318dac] tw-font-bold">
                                                                {translateSection(section)}
                                                            </span>
                                                        </AccordionTrigger>
                                                        <AccordionContent>
                                                            {section.attributes.map((a: any) => {
                                                                if (
                                                                    item[a.name] !== '' &&
                                                                    item[a.name] !== undefined &&
                                                                    !isEmpty(item[a.name])
                                                                ) {
                                                                    return <CatalogAttribute key={a.name} attribute={a} item={item} />;
                                                                }
                                                                return null;
                                                            })}
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                );
                                            }
                                            return null;
                                        })}
                                    </div>
                                </Accordion>
                            )}
                        </div>
                    </div>
                )
            )}
        </div>
    );
}

export { ItemDetails, type ItemDetailsComponentProps };
